<template>
  <div class="edas-questionnaire">
    <!-- Introduction -->
    <div v-if="!startQuestionnaireValue" class="intro-section">
      <div class="intro-header">
        <h2>Échelle de Dépression et d'Anxiété (EDAS)</h2>
        <p class="intro-description">
          Indiquez ce qui correspond le mieux à votre ressenti de la dernière semaine.
        </p>
      </div>

      <div class="legend-card">
        <h3>Échelle de réponses :</h3>
        <div class="legend-items">
          <div class="legend-item">
            <span class="legend-number">0</span>
            <span class="legend-text">Pas du tout</span>
          </div>
          <div class="legend-item">
            <span class="legend-number">1</span>
            <span class="legend-text">Un peu</span>
          </div>
          <div class="legend-item">
            <span class="legend-number">2</span>
            <span class="legend-text">Beaucoup</span>
          </div>
          <div class="legend-item">
            <span class="legend-number">3</span>
            <span class="legend-text">Énormément</span>
          </div>
        </div>
      </div>

      <div class="start-button-container">
        <button @click="startQuestionnaire" class="start-button">
          <span>Commencer</span>
        </button>
      </div>
    </div>

    <!-- Question -->
    <div v-if="startQuestionnaireValue && !IsFinish" class="question-section">
      <div class="question-header">
        <div class="progress-info">
          <span class="question-counter">
            {{ currentQuestionIndex + 1 }} / {{ numberOfQuestions }}
          </span>
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <div class="question-card">
        <h3 class="question-text">
          {{ questions[currentQuestionIndex].name }}
        </h3>

        <div class="answers-grid">
          <label
            v-for="answer in questions[currentQuestionIndex].answers"
            :key="answer.id"
            class="answer-option"
            :class="{ selected: localPicked === answer.points }"
          >
            <input
              type="radio"
              :value="answer.points"
              v-model="localPicked"
              class="answer-radio"
            />
            <div class="answer-content">
              <span class="answer-number">{{ answer.points }}</span>
              <span class="answer-text">{{ answer.text }}</span>
            </div>
          </label>
        </div>

        <div class="navigation-area">
          <button
            v-if="currentQuestionIndex > 0"
            @click="prevQuestion"
            class="nav-button secondary"
          >
            ← Précédent
          </button>

          <button
            v-if="currentQuestionIndex < questions.length - 1"
            @click="NextQuestion(localPicked, questions[currentQuestionIndex])"
            class="nav-button primary"
            :disabled="localPicked === null"
          >
            Suivant →
          </button>

          <button
            v-if="currentQuestionIndex === questions.length - 1"
            @click="TerminerExerciceQCM(questionnaireSelectedId, resultat)"
            class="nav-button finish"
            :disabled="localPicked === null"
          >
            Terminer
          </button>
        </div>
      </div>
    </div>

    <!-- Terminé -->
    <div v-if="IsFinish && !isAnonymousMode" class="results-section">
      <div class="success-icon">✓</div>
      <h3>Évaluation terminée !</h3>
      <p>Merci d'avoir complété ce questionnaire.</p>
    </div>
  </div>
</template>

<script>
import { getEDAS } from "../../services/bfp/getEDAS";
import { findQuestionsByQuestionnaireId } from "@/services/bfp/findQuestionsByQuestionnaireId";
import Parse from "parse";
import { createNotificationForBeginQuestionnaire } from "@/services/notifications/createNotificationForBeginQuestionnaire.js";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "EdasComponent",
  props: {
    isForBeginPage: {
      type: Boolean,
      default: false,
    },
    idPatientTherapeute: {
      type: String,
      default: null,
    },
    isAnonymousMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      questionnaireBFP: [],
      questionnaireSelected: {},
      questionnaireSelectedId: "",
      questions: [],
      startQuestionnaireValue: false,
      currentQuestionIndex: 0,
      localPicked: null,
      answers: [],
      resultat: 0,
      categoryScores: {},
      startTime: null,
      endTime: null,
      IsFinish: false,
    };
  },
  computed: {
    numberOfQuestions() {
      return this.questions.length;
    },
    progressPercentage() {
      return ((this.currentQuestionIndex + 1) / this.numberOfQuestions) * 100;
    },
  },
  async created() {
    this.questionnaireBFP = await getEDAS();
    if (this.isForBeginPage) {
      this.openForm(this.questionnaireBFP[0]);
    }
  },
  methods: {
    async startQuestionnaire() {
      this.startQuestionnaireValue = true;
      this.startTime = new Date();
    },

    openForm(questionnaire) {
      this.questionnaireSelectedId = questionnaire.id;
      this.questionnaireSelected = questionnaire;
      this.findQuestion(questionnaire.id);
    },

    async findQuestion(id) {
      try {
        const results = await findQuestionsByQuestionnaireId(id);
        this.questions = results;
      } catch (error) {
        console.error("Error while fetching questions:", error);
      }
    },

    NextQuestion(picked, question) {
  if (picked === null) {
    Swal.fire({
      icon: "warning",
      title: "Attention",
      text: "Veuillez sélectionner une réponse avant de continuer.",
      confirmButtonColor: "#239380",
    });
    return;
  }

  // 1️⃣ On enregistre la réponse
  this.answers.push({
    question_id: question.id,
    question: question.name,
    note: picked,
  });
  this.resultat += picked;

  // 2️⃣ On met à jour les scores par catégorie (Stress / Anxiété / Dépression)
  if (question.category) {
    const categoryName = question.category.name;
    if (!this.categoryScores[categoryName]) {
      this.categoryScores[categoryName] = 0;
    }
    this.categoryScores[categoryName] += picked;
  }

  // 3️⃣ Évènement spécifique EDAS (si tu veux suivre ça ailleurs)
  this.$emit("edas-scores-updated", {
    total: this.resultat,
    categoryScores: { ...this.categoryScores },
  });

  // 4️⃣ 🔥 Mise à jour temps réel pour le bandeau latéral (Bilans)
  this.$emit("bilans-realtime", {
    Stress: this.categoryScores["Stress"] || 0,
    Anxiété: this.categoryScores["Anxiété"] || 0,
    Dépression: this.categoryScores["Dépression"] || 0,
    Épuisement: 0,
    Tension: 0,
    Moral: 0,
  });

  // 5️⃣ On passe à la question suivante / fin de questionnaire
  this.localPicked = null;

  if (this.currentQuestionIndex < this.questions.length - 1) {
    this.currentQuestionIndex++;
  } else {
    this.IsFinish = true;
    this.endTime = new Date();

    if (this.isAnonymousMode) {
      setTimeout(() => {
        this.TerminerExerciceQCM(this.questionnaireSelectedId, this.resultat);
      }, 500);
    }
  }
},

    prevQuestion() {
      if (this.currentQuestionIndex > 0) {
        const lastAnswer = this.answers.pop();
        this.resultat -= lastAnswer.note;

        const question = this.questions[this.currentQuestionIndex];
        if (question.category) {
          const categoryName = question.category.name;
          this.categoryScores[categoryName] -= lastAnswer.note;
        }

        // 🌟 Mise à jour temps réel
        this.$emit("edas-scores-updated", {
          total: this.resultat,
          categoryScores: { ...this.categoryScores },
        });

        this.currentQuestionIndex--;
        this.localPicked = null;
      }
    },

    async TerminerExerciceQCM(fileId, note) {
      this.$emit("EnableButton");

      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      // Mode anonyme : émettre les données sans sauvegarde
      if (this.isAnonymousMode) {
        const bilanData = {
          totalScore: note,
          globalScore: note,
          categoryScores: this.categoryScores,
          answers: this.answers,
          startTime: this.startTime,
          endTime: new Date(),
          fileId: fileId,
          bilan: this.calculateBilan(note),
          details: `Bilan EDAS - Score: ${note}`,
          questionnaireType: "EDAS",
          questionnaireName:
            this.questionnaireSelected?.attributes?.name || "EDAS",
        };

        this.$emit("BilanCompleted", bilanData);
        this.$emit("EnableButton");

        Toast.fire({
          icon: "success",
          title: "Évaluation EDAS terminée avec succès",
        });

        this.answers = [];
        this.IsFinish = true;
        return;
      }

      // MODE NORMAL : Sauvegarde existante
      let userId;
      if (this.idPatientTherapeute) {
        userId = this.idPatientTherapeute;
      } else if (localStorage.getItem("idUserForSave")) {
        userId = localStorage.getItem("idUserForSave");
      } else {
        userId = Parse.User.current().id;
      }

      try {
        const result = await Parse.Cloud.run("terminerExerciceBFP", {
          fileId,
          note,
          categoryScores: this.categoryScores,
          startTime: this.startTime,
          userId,
        });

        createNotificationForBeginQuestionnaire(result.file, note);
        this.answers = [];
        this.$emit("EnableButton");

        Toast.fire({
          icon: "success",
          title: "Votre questionnaire a été envoyé avec succès",
        });
      } catch (error) {
        Toast.fire({
          icon: "error",
          title: "Une erreur est survenue lors de l'envoi du questionnaire",
        });
        console.error("Error while creating Data: ", error);
      }

      this.$emit("GoToActivity");
    },

    calculateBilan(score) {
      if (score <= 20) {
        return "vert";
      } else if (score <= 40) {
        return "orange";
      } else {
        return "rouge";
      }
    },
  },
};
</script>

<style scoped>
.edas-questionnaire {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px 0 0 0;
  font-family: "Segoe UI", sans-serif;
}

/* Introduction Section */
.intro-section {
  text-align: center;
  margin-bottom: 30px;
}

.intro-header {
  margin-bottom: 30px;
}

.intro-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #239380, #1c7a6a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: white;
  box-shadow: 0 6px 20px rgba(35, 147, 128, 0.3);
}

.intro-header h2 {
  font-size: 28px;
  color: #239380;
  margin-bottom: 15px;
  font-weight: 600;
}

.intro-description {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.legend-card {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid #e8f5f3;
  margin-bottom: 30px;
  text-align: left;
}

.legend-card h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
  text-align: center;
}

.legend-items {
  display: grid;
  gap: 12px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  background: #f8fafa;
  border-radius: 8px;
}

.legend-number {
  width: 32px;
  height: 32px;
  background: #239380;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
}

.legend-text {
  font-size: 14px;
  color: #555;
  line-height: 1.4;
}

.start-button-container {
  margin-top: 30px;
}

.start-button {
  background: linear-gradient(135deg, #239380, #1c7a6a);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 18px 36px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 6px 20px rgba(35, 147, 128, 0.3);
}

.start-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(35, 147, 128, 0.4);
}

/* Question Section */
.question-section {
  margin-bottom: 30px;
}

.question-header {
  margin-bottom: 25px;
}

.progress-info {
  text-align: center;
}

.question-counter {
  display: inline-block;
  background: #239380;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #239380, #1c7a6a);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.question-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid #e8f5f3;
}

.question-text {
  font-size: 20px;
  color: #333;
  margin-bottom: 30px;
  line-height: 1.5;
  font-weight: 500;
  text-align: center;
}

.answers-grid {
  display: grid;
  gap: 15px;
  margin-bottom: 30px;
}

.answer-option {
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  position: relative;
}

.answer-option:hover {
  border-color: #239380;
  background: #f0f9ff;
  transform: translateY(-2px);
}

.answer-option.selected {
  border-color: #239380;
  background: linear-gradient(135deg, #f0f9ff, #e8f5f3);
  box-shadow: 0 4px 12px rgba(35, 147, 128, 0.2);
}

.answer-radio {
  display: none;
}

.answer-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.answer-number {
  width: 40px;
  height: 40px;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  color: #666;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.answer-option.selected .answer-number {
  background: #239380;
  color: white;
}

.answer-text {
  font-size: 16px;
  color: #333;
  line-height: 1.4;
}

.navigation-area {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 30px;
}

.nav-button {
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  justify-content: center;
}

.nav-button.secondary {
  background: #f5f5f5;
  color: #666;
  border: 2px solid #e5e5e5;
}

.nav-button.secondary:hover {
  background: #e5e5e5;
  transform: translateY(-2px);
}

.nav-button.primary {
  background: #239380;
  color: white;
}

.nav-button.primary:hover:not(:disabled) {
  background: #1c7a6a;
  transform: translateY(-2px);
}

.nav-button.finish {
  background: linear-gradient(135deg, #28a745, #218838);
  color: white;
}

.nav-button.finish:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Results Section */
.results-section {
  text-align: center;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid #e8f5f3;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #28a745, #218838);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 36px;
  color: white;
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.3);
}

.results-section h3 {
  font-size: 24px;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.results-section p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
}

/* Responsive */
@media (max-width: 768px) {
  .edas-questionnaire {
    padding: 15px;
  }

  .intro-header h2 {
    font-size: 24px;
  }

  .question-card {
    padding: 20px;
  }

  .question-text {
    font-size: 18px;
  }

  .answer-option {
    padding: 15px;
  }

  .navigation-area {
    flex-direction: column;
  }

  .nav-button {
    width: 100%;
  }

  .legend-item {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .intro-header h2 {
    font-size: 20px;
  }

  .intro-description {
    font-size: 14px;
  }

  .question-text {
    font-size: 16px;
  }

  .answer-content {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
}
</style>